import { brazeService } from './brazeService';
import type { ConsentState } from './types';

class BrazeConsentManager {
  private static instance: BrazeConsentManager;
  private hasInitialized = false;
  private consentListenersAttached = false;

  private constructor() {}

  public static getInstance(): BrazeConsentManager {
    if (!BrazeConsentManager.instance) {
      BrazeConsentManager.instance = new BrazeConsentManager();
    }
    return BrazeConsentManager.instance;
  }

  public initialize(): void {
    if (this.hasInitialized) return;
    this.hasInitialized = true;

    if (this.shouldBypassConsent()) {
      this.initializeBraze();
      return;
    }

    if (!window.Cookiebot) {
      this.waitForCookiebot();
      return;
    }

    this.attachConsentListeners();

    if (window.Cookiebot.hasResponse) {
      this.handleConsentState(this.getCurrentConsentState());
    }
  }

  private shouldBypassConsent(): boolean {
    const params = new URLSearchParams(window.location.search);
    const isMacOS = params.get('platform') === 'macos';
    const isCesMode =
      params.get('mode') === 'ces' ||
      localStorage.getItem('napster_ces_mode') === 'true';
    return isMacOS || isCesMode;
  }

  private waitForCookiebot(): void {
    const checkInterval = setInterval(() => {
      if (window.Cookiebot) {
        clearInterval(checkInterval);
        this.attachConsentListeners();

        if (window.Cookiebot.hasResponse) {
          this.handleConsentState(this.getCurrentConsentState());
        }
      }
    }, 100);

    setTimeout(() => {
      clearInterval(checkInterval);
      if (!window.Cookiebot) {
        this.initializeBraze();
      }
    }, 10000);
  }

  private attachConsentListeners(): void {
    if (this.consentListenersAttached) return;

    this.consentListenersAttached = true;

    window.addEventListener('CookiebotOnAccept', () => {
      this.handleConsentState(this.getCurrentConsentState());
    });

    window.addEventListener('CookiebotOnDecline', () => {
      this.handleConsentDecline();
    });
  }

  private getCurrentConsentState(): ConsentState {
    return {
      necessary: window.Cookiebot?.consent?.necessary ?? true,
      statistics: window.Cookiebot?.consent?.statistics ?? false,
      marketing: window.Cookiebot?.consent?.marketing ?? false,
      preferences: window.Cookiebot?.consent?.preferences ?? false,
    };
  }

  private handleConsentState(consent: ConsentState): void {
    if (consent.statistics || consent.marketing) {
      this.initializeBraze();
    }
  }

  private handleConsentDecline(): void {
    brazeService.disable();
  }

  private async initializeBraze(): Promise<void> {
    try {
      await brazeService.initialize();
    } catch (error) {
      console.error('[Braze] Failed to initialize:', error);
    }
  }

  public hasConsent(): boolean {
    if (this.shouldBypassConsent()) return true;
    if (!window.Cookiebot?.hasResponse) return false;

    const consent = this.getCurrentConsentState();
    return consent.statistics || consent.marketing;
  }

  public isReady(): boolean {
    return this.hasConsent() && brazeService.isInitialized();
  }
}

export const brazeConsentManager = BrazeConsentManager.getInstance();
